// src/components/RankingItem.tsx
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Participant } from '../data/eurovisionData';

interface RankingItemProps {
  id: string;  // Sortable ID
  participant: Participant;
  index: number;
  onRemove: () => void;
  onShowDetails: (participant: Participant) => void;
  onReplace: () => void;
}

export function RankingItem({ 
  id, 
  participant, 
  index, 
  onRemove, 
  onShowDetails, 
  onReplace 
}: RankingItemProps) {
  const { 
    attributes, 
    listeners, 
    setNodeRef, 
    transform, 
    transition,
    isDragging
  } = useSortable({ id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
  };
  
  // Ensure participant data is valid
  if (!participant) {
    console.error("Missing participant data in RankingItem");
    return null;
  }
  
  return (
    <div ref={setNodeRef} style={style} className="mb-3">
      <div 
        className={`flex items-center bg-white rounded-lg py-3 px-0 md:px-2 shadow-sm border ${
          isDragging ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-100 hover:border-gray-300'
        } cursor-pointer transition-colors`}
      >
        {/* Ranking number */}
        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center font-bold text-lg px-1 md:px-0 md:mr-2">
          {index + 1}
        </div>
        
        {/* Clickable content area for replace action */}
        <div 
        className="flex flex-grow items-center rounded-md py-1 -my-1" 
        onClick={() => {
            // Before opening the modal for replacement, make sure we're starting fresh
            onReplace();
        }}
        >
          {/* Participant image with flag in corner */}
          <div className="relative flex-shrink-0 w-[100px] h-[100px] mr-5 md:mr-7">
            <img 
              src={`${process.env.PUBLIC_URL}/${participant.image}`}
              alt={participant.name} 
              className="w-full h-full object-cover rounded-md"
            />
            
            {/* Country flag - positioned in bottom right corner */}
            <div className="absolute -bottom-2 -right-2">
              <img 
                src={`${process.env.PUBLIC_URL}/flags/${participant.countryCode}.png`} 
                alt={`${participant.country} flag`}
                className="w-9 h-9"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                }}
              />
              {/* Fallback heart with country code */}
              <div className="hidden w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow-sm">
                {participant.countryCode}
              </div>
            </div>
          </div>
          
          {/* Content column - info and odds */}
          <div className="flex-grow flex flex-col">
            {/* Participant info */}
            <div>
              <div className="font-bold">{participant.country}</div>
              <div className="text-sm">{participant.name}</div>
              <div className="text-xs text-gray-500">{participant.songTitle}</div>
            </div>
            
            {/* Odds - now below the info */}
            <div className="mt-2">
              <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded inline-flex items-center">
                <span className="mr-1">Odds to win:</span>
                <span className="font-bold">{participant.odds.toFixed(2)}</span>
                <img 
                    src={`${process.env.PUBLIC_URL}/img/bet365.png`}
                    alt="Sportsbook"
                    className="w-4 h-4 rounded ml-2"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex flex-col items-center gap-1 ml-1">
          {/* Drag handle */}
          <div 
            {...attributes} 
            {...listeners} 
            className="cursor-grab p-2 touch-manipulation"
            style={{ touchAction: 'none' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
            </svg>
          </div>
          
          {/* Info button */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering onReplace
              onShowDetails(participant);
            }}
            className="p-2 text-blue-600 rounded hover:bg-blue-50"
            title="View details"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          
          {/* Remove button */}
          <button 
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering onReplace
              onRemove();
            }} 
            className="p-2 text-red-600 rounded hover:bg-red-50"
            title="Remove from ranking"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}